import {
  Object3D,
  Group,
  SphereGeometry,
  MeshBasicMaterial,
  Mesh,
  Vector3,
  Quaternion,
  Material,
} from "three";

import { GLTFLoader } from "three-stdlib";

import { Context } from "../context";
import { SceneObjectBase } from "./object";
import { Frames } from "../util/frame";
import { InferredBatPosition } from "../../lib/types/statsApi";
import { calculateAlpha } from "../util/clock";

const TRANSITION_CEILING = 500;

const batQuat = new Quaternion();
const unitForward = new Vector3(0, -1.0, 0);

export class Bat extends SceneObjectBase {
  contextRef: Context;
  group = new Group();
  handle: Object3D;
  head: Object3D;
  topHand: Object3D;
  bottomHand: Object3D;
  bat: Object3D | undefined;
  batMaterial: Material | undefined;

  firstFrameTime?: number = undefined;
  lastFrameTime?: number = undefined;

  topHandPercent = 0.15;
  bottomHandPercent = 0.05;

  constructor(context: Context) {
    super();
    this.contextRef = context;

    const handleGeometry = new SphereGeometry(0.1, 32, 16);
    const handleMaterial = new MeshBasicMaterial({ color: 0xffff00 });
    this.handle = new Mesh(handleGeometry, handleMaterial);

    const headGeometry = new SphereGeometry(0.2, 32, 16);
    const headMaterial = new MeshBasicMaterial({ color: 0xffff00 });
    this.head = new Mesh(headGeometry, headMaterial);

    const handMaterial = new MeshBasicMaterial({ color: 0xff10f0 });
    this.topHand = new Mesh(handleGeometry, handMaterial);
    this.bottomHand = new Mesh(handleGeometry, handMaterial);

    this.handle.visible = false;
    this.head.visible = false;
    this.topHand.visible = false;
    this.bottomHand.visible = false;

    this.group.add(this.head);
    this.group.add(this.handle);
    this.group.add(this.topHand);
    this.group.add(this.bottomHand);

    const loader = new GLTFLoader().setPath(`${context.options.assetBase}`);
    loader.load("/models/bat.glb", (model) => {
      this.bat = model.scene;
      this.bat.traverse((obj) => {
        if (obj.type === "Mesh") {
          // @ts-ignore
          this.batMaterial = obj.material;
          this.batMaterial!.alphaTest = 0.01;
          this.batMaterial!.transparent = true;
          this.batMaterial!.opacity = 0.0;
        }
      });
      this.bat.position.set(-10, -10, -10);
      this.bat.receiveShadow = true;
      this.group.add(this.bat);
      this.group.visible = false;
    });
  }

  get object(): Object3D {
    return this.group;
  }

  update(frames: Frames, time: number) {
    if (
      frames.current &&
      frames.current.inferredBat?.head_position &&
      frames.current.inferredBat?.handle_position
    ) {
      this.handleFade(time, true);
      this.group.visible = this.contextRef.state.batEnabled;

      const neighbors = frames.getNeighbours<InferredBatPosition>(
        (frame) => frame.inferredBat
      );

      if (
        neighbors &&
        neighbors.prev?.time != undefined &&
        neighbors.next?.time != undefined
      ) {
        const alpha = calculateAlpha(
          [neighbors.prev.time, neighbors.next.time],
          time
        );
        if (
          alpha != undefined &&
          neighbors.next.neighbor.head_position &&
          neighbors.next.neighbor.handle_position
        ) {
          let interpBat = interpolateBat(
            neighbors.prev.neighbor,
            neighbors.next.neighbor,
            alpha
          );
          if (interpBat) {
            this.head.position.set(
              interpBat.head_position.x,
              interpBat.head_position.z,
              -interpBat.head_position.y
            );
            this.handle.position.set(
              interpBat.handle_position.x,
              interpBat.handle_position.z,
              -interpBat.handle_position.y
            );

            this.topHand.position.lerpVectors(
              this.handle.position,
              this.head.position,
              this.topHandPercent
            );
            this.bottomHand.position.lerpVectors(
              this.handle.position,
              this.head.position,
              this.bottomHandPercent
            );
          }
        }
      }

      const batLengthActual =
        this.head.position.distanceTo(this.handle.position) / 12 / 6;

      batQuat.setFromUnitVectors(
        unitForward,
        new Vector3()
          .subVectors(this.handle.position, this.head.position)
          .normalize()
      );

      this.bat?.setRotationFromQuaternion(batQuat);

      this.bat?.position.lerpVectors(
        this.head.position,
        this.handle.position,
        0.5
      );
      this.bat?.scale.set(batLengthActual, batLengthActual, batLengthActual);
    } else {
      this.handleFade(time, false);
    }
  }

  handleFade(time: number, during: boolean) {
    if (!this.batMaterial) return;

    if (during) {
      if (!this.firstFrameTime) this.firstFrameTime = time;
      const fade = Math.min(time - this.firstFrameTime, TRANSITION_CEILING);
      this.batMaterial.opacity = fade / TRANSITION_CEILING;
      return;
    }

    if (!during && this.firstFrameTime) {
      if (!this.lastFrameTime) this.lastFrameTime = time;
      const fade = Math.min(time - this.lastFrameTime, TRANSITION_CEILING);
      this.batMaterial.opacity = 1.0 - fade / TRANSITION_CEILING;

      if (this.batMaterial.opacity <= 0) {
        this.firstFrameTime = undefined;
        this.lastFrameTime = undefined;
      }
      return;
    }

    this.firstFrameTime = undefined;
    this.lastFrameTime = undefined;
    this.batMaterial.opacity = 0.0;
  }
}

export function interpolateBat(
  prev: InferredBatPosition,
  next: InferredBatPosition,
  alpha: number
): InferredBatPosition | undefined {
  if (prev == undefined || !prev.head_position || !prev.handle_position)
    return undefined;
  if (next == undefined || !next.head_position || !next.handle_position)
    return prev;
  return {
    head_position: {
      x:
        prev.head_position.x +
        alpha * (next.head_position.x - prev.head_position.x),
      y:
        prev.head_position.y +
        alpha * (next.head_position.y - prev.head_position.y),
      z:
        prev.head_position.z +
        alpha * (next.head_position.z - prev.head_position.z),
    },
    handle_position: {
      x:
        prev.handle_position.x +
        alpha * (next.handle_position.x - prev.handle_position.x),
      y:
        prev.handle_position.y +
        alpha * (next.handle_position.y - prev.handle_position.y),
      z:
        prev.handle_position.z +
        alpha * (next.handle_position.z - prev.handle_position.z),
    },
  };
}
